import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Button, Checkbox, Typography } from '@totango-app-team/ui-shared-components';
import capitilize from 'lodash/capitalize';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';

import Input from 'Components/input/input';
import RadioGroup from 'Components/radio-group/radio-group';
import { isLocal } from 'Utils/utils';

import { dataCenterOptions } from '../../utils/constants';
import {
	atLeastEightChars,
	atLeastOneLower,
	atLeastOneNumberOrSymbol,
	atLeastOneUpper,
} from '../../utils/steps-utils';
import PasswordValidatorHelperTooltip from '../password-validator-helper-tooltip/password-validator-helper-tooltip';

import './password-step.less';

export default function PasswordStep(props) {
	const {
		handleNext,
		handlePrev,
		password,
		setPassword,
		setAgreedReceiveContent,
		agreedReceiveContent,
		dataCenter,
		setDataCenter,
		shouldShowDataCenterOptions,
		email,
		generateAuthenticationCode,
		oauthIssuer,
		firstName,
		lastName,
	} = props;

	const [isValidPassword, setIsValidPassword] = useState(false);
	const [passwordType, setPasswordType] = useState('password');
	const [isLoading, setIsLoading] = useState(false);
	const [showPasswordTooltip, setShowPasswordTooltip] = useState(true);
	useEffect(() => {
		if (isValidPassword) {
			const t = setTimeout(() => setShowPasswordTooltip(false), 500);
			return () => clearTimeout(t);
		}
		setShowPasswordTooltip(true);
		return undefined;
	}, [isValidPassword]);
	const oAuthUserTitle = useMemo(
		() => (firstName || lastName ? `Hi ${firstName} ${lastName}` : `Hi ${email}`),
		[email, firstName, lastName],
	);

	const onPasswordChange = useCallback(
		e => {
			e.persist();
			setPassword(e.target.value);
		},
		[setPassword],
	);

	useEffect(() => {
		// password validation
		const validation =
			atLeastEightChars(password) &&
			atLeastOneLower(password) &&
			atLeastOneUpper(password) &&
			atLeastOneNumberOrSymbol(password);
		setIsValidPassword(validation);
	}, [password]);

	const onButtonClick = useCallback(
		async e => {
			setIsLoading(true);
			if (!isValidPassword && !oauthIssuer) {
				setIsLoading(false);
				setIsValidPassword(false);
			} else {
				try {
					generateAuthenticationCode(email, !oauthIssuer);
					if (!oauthIssuer) {
						handleNext();
					}
				} catch (error) {
					setIsLoading(false);
				}
			}
		},
		[isValidPassword, oauthIssuer, generateAuthenticationCode, email, handleNext],
	);

	const onAgreedReceiveContentChange = useCallback(
		e => {
			e.persist();
			setAgreedReceiveContent(e.target.checked);
		},
		[setAgreedReceiveContent],
	);

	const onDataCenterChanged = useCallback(
		e => {
			setDataCenter(e.target.value);
		},
		[setDataCenter],
	);

	const onPasswordEyeClick = useCallback(e => {
		e.preventDefault();
		if (e.button === 0) {
			//left button
			setPasswordType(currentState => (currentState ? '' : 'password'));
		}
	}, []);

	return (
		<Grid container spacing={4} className="step-container create-account password-step-container">
			{!oauthIssuer ? (
				<>
					<Grid item xs={12}>
						<h5 className="step-title" align="center">
							Create your
							<span className="title-underline-container">
								{'  '}
								FREE
								<img src="/img/self-service/green-underline.svg" alt="" />
							</span>
							{'  '}
							account
						</h5>
					</Grid>
					<Grid item xs={12}>
						<PasswordValidatorHelperTooltip
							showPasswordTooltip={showPasswordTooltip}
							password={password}
						>
							<div>
								<Input
									id="signup-password"
									className="signup-input-wrapper"
									InputProps={{
										classes: {
											root: 'signup-input',
										},
									}}
									name="password"
									variant="outlined"
									type={passwordType}
									value={password}
									label="Create a Password"
									fullWidth
									autoFocus
									autoComplete="no"
									onChange={onPasswordChange}
									error={!isValidPassword && password !== ''}
									maxLength={255}
									endAdornment={
										<Button
											onMouseDown={onPasswordEyeClick}
											onClick={noop}
											className="present-password-icon"
										>
											<img src="/img/self-service/see-password.svg" alt="" />
										</Button>
									}
								/>
							</div>
						</PasswordValidatorHelperTooltip>
					</Grid>
				</>
			) : (
				<Grid item xs={12} className="oauth-issuer-message-container">
					<img src="/img/self-service/green-check.svg" alt="" />
					<Typography variant="h4">{oAuthUserTitle}</Typography>
					<Typography variant="subtitle2" className="oauth-iusser-text">
						<div>{`You successfully connected your ${capitilize(oauthIssuer)} account.`}</div>
						<div>Click below to complete your registration.</div>
					</Typography>
				</Grid>
			)}
			{shouldShowDataCenterOptions && (
				<Grid item xs={12} className="data-center-container">
					<Grid
						container
						className="select-data-center"
						alignItems="center"
						justifyContent="space-between"
					>
						<div className="data-center-label">Select data center location:</div>
						<RadioGroup
							className="data-center-options"
							row
							options={dataCenterOptions}
							onChange={onDataCenterChanged}
							value={dataCenter}
						/>
					</Grid>
				</Grid>
			)}
			<Grid item xs={12} className="relevant-content-checkbox">
				<Checkbox
					checked={agreedReceiveContent}
					onChange={onAgreedReceiveContentChange}
					color="primary"
					label="I agree to receive relevant content from Totango"
				/>
			</Grid>
			<Grid item xs={12}>
				<Button
					color="primary"
					variant="contained"
					className="signup-button"
					label="Continue"
					onClick={onButtonClick}
					disabled={oauthIssuer ? false : !isValidPassword}
					fullWidth
					isLoading={isLoading}
				>
					{'Create Account'}
				</Button>
			</Grid>
			{!oauthIssuer && (
				<Grid item xs={12}>
					<div className="signin-btn-tooltip">
						Already have an account?
						<a
							className="login-link"
							href={
								isLocal()
									? 'https://localhost:8443/debug.html'
									: `https://${window.location.hostname}`
							}
						>
							{' '}
							Login
						</a>
					</div>
				</Grid>
			)}
			<Grid item xs={12} className="contracts">
				<div className="back-to-email">
					<Button color="primary" variant="text" onClick={handlePrev}>
						<div className="button-back">
							<ArrowBackIcon fontSize="small" />
							{'Back to email'}
						</div>
					</Button>
				</div>
				<div>
					By clicking on Create Account, you agree to our
					<a className="link-color" href="https://www.totango.com/terms-of-use">
						{' '}
						Terms of Service
					</a>
				</div>
				<div>
					and you acknowledge heaving read our
					<a className="link-color" href="https://www.totango.com/privacy-policy">
						{' '}
						Privacy Policy
					</a>
				</div>
			</Grid>
		</Grid>
	);
}

PasswordStep.defaultProps = {
	oauthIssuer: undefined,
	dataCenter: '',
};

PasswordStep.propTypes = {
	handleNext: PropTypes.func.isRequired,
	password: PropTypes.string.isRequired,
	setPassword: PropTypes.func.isRequired,
	setAgreedReceiveContent: PropTypes.func.isRequired,
	agreedReceiveContent: PropTypes.bool.isRequired,
	dataCenter: PropTypes.string,
	setDataCenter: PropTypes.func.isRequired,
	shouldShowDataCenterOptions: PropTypes.bool.isRequired,
	email: PropTypes.string.isRequired,
	generateAuthenticationCode: PropTypes.func.isRequired,
	oauthIssuer: PropTypes.string,
	firstName: PropTypes.string.isRequired,
	lastName: PropTypes.string.isRequired,
};
